"use client";

/**
 * "You're booked!" card shown when a Scheduler question has a stored
 * booking value. Used after any Scheduler provider (embed widget or custom
 * picker) confirms a booking.
 *
 * The card auto-advances the form to the next step after booking, so the
 * primary moment it's *visible* is when a respondent navigates back to a
 * question they've already booked. It needs to look like a confirmation,
 * not a placeholder.
 *
 * Visuals: compact inverted panel matching the scheduler booker widget,
 * with a date tile, slot summary, invitee email, and action links.
 */

import { motion } from "framer-motion";
import Icon from "../../../../atoms/Icon";

interface Props {
  startTime: string;
  endTime: string;
  inviteeName?: string;
  inviteeEmail?: string;
  meetUrl?: string | null;
  externalUrl?: string | null;
  /** When true, the booking still needs external confirmation (Calendly
   *  hosted-page fallback). The card shows different copy + CTAs. */
  isPending?: boolean;
}

const MONTH_SHORT = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

const formatTime = (d: Date) =>
  d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

const formatTimezoneLabel = (timezone: string) => {
  try {
    const parts = new Intl.DateTimeFormat(undefined, {
      timeZone: timezone,
      timeZoneName: "shortGeneric",
    }).formatToParts(new Date());
    return parts.find((p) => p.type === "timeZoneName")?.value ?? timezone;
  } catch {
    return timezone;
  }
};

const resolveTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return "UTC";
  }
};

const BookingConfirmationCard = ({
  startTime,
  endTime,
  inviteeEmail,
  meetUrl,
  externalUrl,
  isPending = false,
}: Props) => {
  const start = new Date(startTime);
  const end = new Date(endTime);
  const timezone = resolveTimezone();
  const timezoneLabel = formatTimezoneLabel(timezone);

  const dayOfMonth = start.getDate();
  const monthShort = MONTH_SHORT[start.getMonth()];
  const year = start.getFullYear();
  const shortDate = start.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });

  return (
    <motion.div
      className="bookingCard"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
      data-pending={isPending || undefined}
    >
      <div className="bookingCard__status">
        <span className="bookingCard__statusIcon" aria-hidden>
          <Icon name={isPending ? "clock-1" : "check"} />
        </span>
        <span className="bookingCard__statusLabel">
          {isPending ? "Almost booked" : "You're booked"}
        </span>
      </div>

      <div className="bookingCard__event">
        <div className="bookingCard__dateTile" aria-hidden>
          <span className="bookingCard__dateTileMonth">{monthShort}</span>
          <span className="bookingCard__dateTileDay">{dayOfMonth}</span>
          <span className="bookingCard__dateTileYear">{year}</span>
        </div>
        <div className="bookingCard__eventDetails">
          <span className="bookingCard__eventDate">{shortDate}</span>
          <span className="bookingCard__eventTime">
            {formatTime(start)} – {formatTime(end)} · {timezoneLabel}
          </span>
        </div>
      </div>

      {inviteeEmail && (
        <div className="bookingCard__invitee">
          <span className="bookingCard__inviteeLabel">Booked as</span>
          <span className="bookingCard__inviteeValue">{inviteeEmail}</span>
        </div>
      )}

      {(meetUrl || externalUrl) && (
        <div className="bookingCard__actions">
          {!isPending && meetUrl && (
            <a
              className="bookingCard__primaryAction"
              href={meetUrl}
              target="_blank"
              rel="noreferrer noopener"
            >
              <Icon className="bookingCard__inlineIcon" name="camera-video" />
              <span>Join meeting</span>
            </a>
          )}
          {isPending && externalUrl && (
            <a
              className="bookingCard__primaryAction"
              href={externalUrl}
              target="_blank"
              rel="noreferrer noopener"
            >
              <Icon className="bookingCard__inlineIcon" name="arrow-up-right" />
              <span>Confirm on Calendly</span>
            </a>
          )}
          {!isPending && externalUrl && (
            <a
              className="bookingCard__secondaryAction"
              href={externalUrl}
              target="_blank"
              rel="noreferrer noopener"
            >
              <Icon className="bookingCard__inlineIcon" name="link-1" />
              <span>Manage booking</span>
            </a>
          )}
        </div>
      )}
    </motion.div>
  );
};

export default BookingConfirmationCard;
